import { Controller } from "@hotwired/stimulus"
import Modal from "bootstrap/js/dist/modal"

// Looks up an existing player by FFA ID while filling in the "Add New
// Player" form on a squad page, so a manager transferring a player who's
// already registered (possibly at another club) doesn't create a duplicate
// Player record — they can instead pull the existing one into their club,
// or if the player's already in their club, just add them to this squad.
export default class extends Controller {
  static targets = [
    "foundCard",
    "foundName",
    "foundClub",
    "createButton",
    "otherClubInfo",
    "sameClubInfo",
    "transferButton",
    "addToSquadButton",
    "nameFields",
  ]
  static values = { lookupUrl: String, transferBaseUrl: String, teamId: Number }

  connect() {
    this.reset()
    this.element.addEventListener("show.bs.modal", this.reset.bind(this))
  }

  reset() {
    this.foundPlayer = null
    this.foundCardTarget.classList.add("d-none")
    this.createButtonTarget.classList.remove("d-none")
    this.nameFieldsTarget.classList.remove("d-none")
  }

  async lookup(event) {
    const ffaId = event.target.value.trim()

    if (!ffaId) {
      this.reset()
      return
    }

    const url = new URL(this.lookupUrlValue, window.location.origin)
    url.searchParams.set("ffa_id", ffaId)
    url.searchParams.set("team_id", this.teamIdValue)

    let json
    try {
      const response = await fetch(url, { headers: { Accept: "application/json" } })
      json = await response.json()
    } catch (error) {
      this.reset()
      return
    }

    if (json.found) {
      this.showFound(json.player)
    } else {
      this.reset()
    }
  }

  showFound(player) {
    this.foundPlayer = player
    this.foundNameTarget.textContent = player.text
    this.foundClubTarget.textContent = player.club_name

    this.otherClubInfoTarget.classList.toggle("d-none", player.same_club)
    this.sameClubInfoTarget.classList.toggle("d-none", !player.same_club)
    this.transferButtonTarget.classList.toggle("d-none", player.same_club)
    this.addToSquadButtonTarget.classList.toggle("d-none", !player.same_club)

    this.foundCardTarget.classList.remove("d-none")
    this.createButtonTarget.classList.add("d-none")
    this.nameFieldsTarget.classList.add("d-none")
  }

  // Not a real <form> submit — the found-player card lives inside the main
  // "Add New Player" <form>, and nesting a second <form> in there isn't
  // valid HTML. A plain fetch here lets the card sit right under the FFA ID
  // field instead of being pushed outside the form entirely.
  async transfer() {
    if (!this.foundPlayer) return

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    const response = await fetch(`${this.transferBaseUrlValue}/${this.foundPlayer.id}/transfer`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
        "X-CSRF-Token": csrfToken,
      },
      body: JSON.stringify({ team_id: this.teamIdValue }),
    })
    const json = await response.json()
    if (!json.success) return

    this.dispatchSuccessAndClose(json)
  }

  // Player's already in this club — no server mutation needed, just select
  // them in the squad row the same way a freshly created/transferred player
  // would be.
  addToSquad() {
    if (!this.foundPlayer) return

    this.dispatchSuccessAndClose({ success: true, player: this.foundPlayer, partials: {} })
  }

  // Reuses the sibling modal-form controller's own success handling
  // (closing the modal + swapping in response partials) rather than
  // duplicating it here.
  dispatchSuccessAndClose(response) {
    const modalForm = this.application.getControllerForElementAndIdentifier(this.element, "modal-form")

    this.element.dispatchEvent(new CustomEvent("modal-form:success", { detail: response, bubbles: true }))
    this.element.addEventListener(
      "hidden.bs.modal",
      () => modalForm.renderPartials(response.partials),
      { once: true }
    )
    Modal.getOrCreateInstance(this.element).hide()
  }
}
